import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cx } from "./cx";
import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** `primary` pour l'action principale d'un écran — une seule par écran. */
  variant?: ButtonVariant;
  /** Défaut `md` (2,5 rem de haut, la cible minimale du projet). */
  size?: ButtonSize;
  /**
   * Envoi en cours : affiche un rouet, désactive le bouton et pose
   * `aria-busy`. À brancher sur le `pending` de `useApiMutation` — c'est ce
   * qui empêche le double clic, et donc la double commande sur
   * `orderMission`, qui n'est pas idempotent.
   */
  loading?: boolean;
  /** Pictogramme avant le libellé. Purement décoratif : il n'est pas annoncé. */
  iconLeft?: ReactNode;
  /** Pictogramme après le libellé. */
  iconRight?: ReactNode;
  /** Occupe toute la largeur disponible. */
  fullWidth?: boolean;
};

/**
 * Bouton d'action.
 *
 * Rend un `<button type="button">` par défaut : un bouton dans un formulaire
 * vaut `submit` sans qu'on le demande, et envoie le formulaire au premier clic.
 */
export function Button({
  variant = "secondary",
  size = "md",
  loading = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        styles.bouton,
        styles[variant],
        size !== "md" && styles[size],
        fullWidth && styles.fullWidth,
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className={styles.rouet} aria-hidden="true" />
      ) : (
        iconLeft && (
          <span className={styles.icone} aria-hidden="true">
            {iconLeft}
          </span>
        )
      )}
      {children}
      {iconRight && !loading && (
        <span className={styles.icone} aria-hidden="true">
          {iconRight}
        </span>
      )}
    </button>
  );
}
